import { Search, ArrowRight } from 'lucide-react';
import { ORB_GRADIENT } from '../constants';

const RING_RESTING = '0 0 0 4px var(--spectral-violet-10), 0 10px 26px -14px rgba(126,95,224,.32)';
const RING_ACTIVE = '0 0 0 4px var(--spectral-violet-20), 0 12px 30px -12px rgba(126,95,224,.44)';

// Floating prompt bar (DC §4.3): persistent violet glow ring, ⌘K hint, gradient
// send orb. Submitting (Enter or the orb) runs the analysis via onSubmit.
export function HeroPromptBar({
  value,
  placeholder = 'Type a product name…',
  onChange,
  onSubmit,
  disabled = false,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '0 10px 0 18px',
        height: 62,
        marginBottom: 26,
        boxShadow: RING_RESTING,
        transition: 'box-shadow var(--duration-base) var(--ease-standard)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = RING_ACTIVE)}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = RING_RESTING)}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0 }}>
        <Search style={{ width: 19, height: 19, color: 'var(--muted-foreground)' }} />
      </span>

      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !disabled) onSubmit();
        }}
        onFocus={(e) => {
          const parent = e.currentTarget.parentElement;
          if (parent) parent.style.boxShadow = RING_ACTIVE;
        }}
        onBlur={(e) => {
          const parent = e.currentTarget.parentElement;
          if (parent) parent.style.boxShadow = RING_RESTING;
        }}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: 16.5,
          fontFamily: 'inherit',
          color: 'var(--foreground)',
        }}
      />

      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted-foreground)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '3px 8px',
          flexShrink: 0,
        }}
      >
        ⌘K
      </span>

      <button
        type="button"
        title="Analyze"
        onClick={() => !disabled && onSubmit()}
        disabled={disabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 42,
          height: 42,
          borderRadius: 11,
          border: 'none',
          background: ORB_GRADIENT,
          color: '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          flexShrink: 0,
          opacity: disabled ? 0.55 : 1,
          boxShadow: 'var(--shadow-spectral-violet-glow)',
          transition: 'filter var(--duration-base) var(--ease-standard)',
        }}
        onMouseEnter={(e) => {
          if (!disabled) e.currentTarget.style.filter = 'brightness(1.08)';
        }}
        onMouseLeave={(e) => (e.currentTarget.style.filter = 'none')}
      >
        <ArrowRight style={{ width: 18, height: 18 }} />
      </button>
    </div>
  );
}
